import axios from '../../../../config/axios';
import { AxiosError } from 'axios';
import { Product } from './types';

const API_BASE_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/product-final`;

export interface Service {
  id: number;
  name: string;
  category: string;
  type: string;
  pricePerUnit?: number;
  prices?: Array<{
    id: number;
    price: string;
    unitOfMeasurement: {
      id: number;
      name: string;
    };
  }>;
  deadlineInDays?: number;
}

export interface RawMaterial {
  id: number;
  name: string;
  density?: number;
}

export interface Resource {
  id: number;
  name: string;
  category: string;
  type: string;
  pricePerUnit: number;
  prices?: Array<{
    id: number;
    price: string;
    unitOfMeasurement: {
      id: number;
      name: string;
    };
  }>;
  description: string;
}

export const fetchResources = async (limit: number = 10, offset: number = 0, search?: string): Promise<Resource[]> => {
  try {
    const page = Math.floor(offset / limit) + 1;
    const { data } = await axios.get(`${API_BASE_URL}/lookup/resource`, {
      params: { page, limit, search }
    });
    return data.data || [];
  } catch (error) {
    console.warn('Erro ao buscar recursos:', error);
    return [];
  }
};

export const fetchServices = async (limit: number = 10, offset: number = 0, search?: string, id?: number): Promise<Service[]> => {
  try {
    const page = Math.floor(offset / limit) + 1;
    const params: any = { page, limit, search, id };
    const { data } = await axios.get(`${API_BASE_URL}/lookup/service`, { params });
    return data.data || [];
  } catch (error) {
    console.warn('Erro ao buscar serviços:', error);
    return [];
  }
};

export const fetchRawMaterials = async (limit: number = 10, offset: number = 0, search?: string): Promise<RawMaterial[]> => {
  try {
    const page = Math.floor(offset / limit) + 1;
    const params = { page, limit, search };
    const { data } = await axios.get(`${API_BASE_URL}/lookup/material`, { params });
    return data.data || [];
  } catch (error) {
    console.warn('Erro ao buscar matérias-primas:', error);
    return [];
  }
};

export const fetchProducts = async (limit: number = 10, offset: number = 0, search?: string): Promise<any[]> => {
  try {
    const page = Math.floor(offset / limit) + 1;
    const params = { page, limit, search };
    const { data } = await axios.get(`${API_BASE_URL}/product`, { params });
    return data.data || [];
  } catch (error) {
    console.warn('Erro ao buscar produtos:', error);
    return [];
  }
};

export const ITEMS_PER_PAGE = 10;

export const fetchPaginatedProducts = async ({ pageParam = 1, search, filters }: { pageParam?: number; search?: string; filters?: any[] }) => {
  try {
    const params: any = { page: pageParam };
    if (search) {
      params.search = search;
    }

    if (filters && filters.length > 0) {
      const filtersObject: any = {};
      filters.forEach(filter => {
        filtersObject[filter.key] = {
          value: filter.value,
          filterType: filter.filterType,
          fieldType: filter.fieldType
        };
      });
      params.filters = JSON.stringify(filtersObject);
    }

    const { data } = await axios.get(`${API_BASE_URL}/product`, { params });

    return {
      data: data.data,
      total: data.total,
      page: pageParam,
    };
  } catch (error) {
    console.warn('Erro ao buscar produtos paginados:', error);
    return { data: [], total: 0, page: pageParam };
  }
};

export const fetchScripts = async (limit: number = 10, offset: number = 0, search?: string): Promise<any[]> => {
  try {
    const page = Math.floor(offset / limit) + 1;
    const params = { page, limit, search };
    const { data } = await axios.get(`${API_BASE_URL}/script`, { params });
    return data.data || [];
  } catch (error) {
    console.warn('Erro ao buscar scripts:', error);
    return [];
  }
};

export const fetchScriptsByProductId = async (productId: number, limit: number = 10, offset: number = 0, search?: string): Promise<any[]> => {
  try {
    const page = Math.floor(offset / limit) + 1;
    const params = { page, limit, search, productId };
    const { data } = await axios.get(`${API_BASE_URL}/script`, { params });
    return data.data || [];
  } catch (error) {
    console.warn(`Erro ao buscar scripts para o produto ${productId}:`, error);
    return [];
  }
};

export const createProduct = async (productData: Product): Promise<Product> => {
  try {
    const { data } = await axios.post(`${API_BASE_URL}/product`, productData);
    return data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data?.message || 'Erro ao criar produto');
    }
    throw error;
  }
};

export const updateProduct = async (productId: string, productData: Product): Promise<Product> => {
  try {
    const { id, createdAt, updatedAt, scripts, ...updateData } = productData;

    const { data } = await axios.patch(`${API_BASE_URL}/product/${productId}`, updateData);
    return data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data?.message || 'Erro ao atualizar produto');
    }
    throw error;
  }
};

export const fetchProductById = async (productId: string): Promise<Product> => {
  try {
    const { data: product } = await axios.get(`${API_BASE_URL}/product/${productId}`);

    if (product.scripts) {
      for (const script of product.scripts) {
        if (script.items) {
          for (const item of script.items) {
            if (item.type === 'productFinal' && item.productScript) {
              try {
                const scriptId = typeof item.productScript === 'object' ? item.productScript.id : item.productScript;
                const { data: scriptDetails } = await axios.get(`${API_BASE_URL}/script/${scriptId}`);

                if (scriptDetails) {
                  item.productScriptName = scriptDetails.name;
                  item.productScriptItems = scriptDetails.items || [];
                }
              } catch (e) {
                const scriptId = typeof item.productScript === 'object' ? item.productScript.id : item.productScript;
                console.warn(`Erro ao buscar detalhes do script ${scriptId}:`, e);
              }
            }
          }
        }
      }
    }

    return product;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar produto');
    }
    throw error;
  }
};

export const deleteProduct = async (productId: string): Promise<void> => {
  try {
    await axios.delete(`${API_BASE_URL}/product/${productId}`);
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data?.message || 'Erro ao deletar produto');
    }
    throw error;
  }
};

export const createScript = async (scriptData: any): Promise<any> => {
  try {
    const { data } = await axios.post(`${API_BASE_URL}/script`, scriptData);
    return data;
  } catch (error) {
    if (error instanceof AxiosError) {
      const backendMessage = error.response?.data?.message;
      const status = error.response?.status;

      if (status === 409 || (backendMessage && (backendMessage.includes('script duplicado') || backendMessage.includes('já existe')))) {
        throw new Error(backendMessage || 'Já existe um script com o mesmo conteúdo.');
      } else {
        throw new Error(backendMessage || 'Erro ao criar script');
      }
    }
    throw error;
  }
};

export const updateScript = async (scriptId: string, scriptData: any): Promise<any> => {
  try {
    const { data } = await axios.patch(`${API_BASE_URL}/script/${scriptId}`, scriptData);
    return data;
  } catch (error) {
    if (error instanceof AxiosError) {
      const backendMessage = error.response?.data?.message;
      const status = error.response?.status;

      const enhancedError = new Error(backendMessage || 'Erro ao atualizar script');
      (enhancedError as any).response = { status, data: { message: backendMessage } };

      throw enhancedError;
    }
    throw error;
  }
};

export const fetchScriptById = async (scriptId: string): Promise<any> => {
  try {
    const { data } = await axios.get(`${API_BASE_URL}/script/${scriptId}`);
    return data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar script');
    }
    throw error;
  }
};

export const deleteScript = async (scriptId: number): Promise<void> => {
  try {
    await axios.delete(`${API_BASE_URL}/script/${scriptId}`);
  } catch (error) {
    console.error('Erro ao excluir script:', error);
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data?.message || 'Erro ao excluir script');
    }
    throw error;
  }
};

export const transformProductDataForBackend = (productData: any): any => {
  const transformed = {
    name: productData.name,
    norm: productData.norm,
    quantity: productData.quantity,
  };
  return transformed;
};

export const transformScriptsDataForBackend = (scripts: any[], productId: number): any[] => {

  const transformed = scripts.map((script: any) => {

    const materials: any[] = [];
    const resources: any[] = [];
    const services: any[] = [];
    const products: any[] = [];

    script.items?.forEach((item: any, index: number) => {
      switch (item.type) {
        case 'rawMaterial':
          materials.push({
            materialId: item.rawMaterial?.id,
            weight: item.weight ? parseFloat(String(item.weight)) : undefined,
            idealMeasurements: item.idealMeasurements,
            step: index + 1,
          });
          break;
        case 'resource':
          resources.push({
            resourceId: item.resource?.id,
            weight: item.weight ? parseFloat(String(item.weight)) : undefined,
            step: index + 1,
            unitOfMeasurementId: item.unitOfMeasurementId || null,
          });
          break;
        case 'service':
          services.push({
            serviceId: item.service?.id,
            companyId: item.company?.id || null,
            executionTime: item.executionTime,
            requiredQualification: item.requiredQualification,
            deliveryTime: item.deliveryTime ? String(item.deliveryTime) : undefined,
            step: index + 1,
            unitOfMeasurementId: item.unitOfMeasurementId || null,
          });
          break;
        case 'productFinal':
          products.push({
            productId: item.productFinal?.id,
            productScript: typeof item.productScript === 'object' ? item.productScript.id?.toString() : item.productScript,
            step: index + 1,
          });
          break;
      }
    });

    const transformedScript = {
      name: script.name,
      productId: productId,
      additionalDeadline: script.additionalDeadline,
      materials,
      resources,
      services,
      products,
    };
    return transformedScript;
  });

  return transformed;
};

export const createScriptItem = async (scriptId: number, item: any): Promise<void> => {
  try {

    switch (item.type) {
      case 'resource': {
        const resourceId = item.resource?.id;
        if (!resourceId) {
          return;
        }

        const payload = {
          resourceId: resourceId,
          weight: item.weight ? parseFloat(String(item.weight)) : undefined,
          step: item.order ? parseInt(String(item.order), 10) : undefined,
          unitOfMeasurementId: item.unitOfMeasurementId ? parseInt(String(item.unitOfMeasurementId), 10) : undefined
        };

        await axios.post(`${API_BASE_URL}/item/script/${scriptId}/resource`, payload);
        break;
      }
      case 'service': {
        const serviceId = item.service?.id;
        if (!serviceId) {
          return;
        }

        const payload = {
          serviceId: serviceId,
          companyId: item.company?.id,
          executionTime: item.executionTime,
          requiredQualification: item.requiredQualification,
          deliveryTime: item.deliveryTime ? String(item.deliveryTime) : undefined,
          step: item.order ? parseInt(String(item.order), 10) : undefined,
          unitOfMeasurementId: item.unitOfMeasurementId ? parseInt(String(item.unitOfMeasurementId), 10) : undefined
        };

        await axios.post(`${API_BASE_URL}/item/script/${scriptId}/service`, payload);
        break;
      }
      case 'rawMaterial': {
        const materialId = item.rawMaterial?.id;
        if (!materialId) {
          return;
        }

        const payload = {
          materialId: materialId,
          weight: item.weight ? parseFloat(String(item.weight)) : undefined,
          idealMeasurements: item.idealMeasurements,
          step: item.order ? parseInt(String(item.order), 10) : undefined
        };

        await axios.post(`${API_BASE_URL}/item/script/${scriptId}/material`, payload);
        break;
      }
      case 'productFinal': {
        const productId = item.productFinal?.id;
        if (!productId) {
          return;
        }

        const payload = {
          productId: productId,
          productScript: typeof item.productScript === 'object' ? item.productScript.id?.toString() : item.productScript,
          step: item.order ? parseInt(String(item.order), 10) : undefined
        };

        await axios.post(`${API_BASE_URL}/item/script/${scriptId}/product`, payload);
        break;
      }
    }

  } catch (error) {
    console.error('Erro ao criar item no script:', error);
    if (error instanceof AxiosError) {
      console.error('Response data:', error.response?.data);
      console.error('Response status:', error.response?.status);

      if (error.response?.status === 409) {
        const backendMessage = error.response?.data?.message;
        if (backendMessage && backendMessage.includes('script duplicado')) {
          throw new Error(backendMessage);
        } else {
          throw new Error('Este item já existe no script.');
        }
      } else if (error.response?.status === 400) {
        const backendMessage = error.response?.data?.message;
        throw new Error(backendMessage || 'Dados inválidos para criar item.');
      } else if (error.response?.status === 500) {
        const backendMessage = error.response?.data?.message;
        throw new Error(backendMessage || 'Erro interno do servidor. Tente novamente.');
      } else {
        const backendMessage = error.response?.data?.message;
        throw new Error(backendMessage || 'Erro ao criar item no script');
      }
    }
    throw error;
  }
};

export const deleteScriptItem = async (scriptId: number, item: any): Promise<void> => {
  try {
    const numericId = item.realId || (typeof item.id === 'number' ? item.id : parseInt(item.id, 10));
    if (!numericId || isNaN(numericId)) return;

    switch (item.type) {
      case 'resource':
        await axios.delete(`${API_BASE_URL}/item/resource/${numericId}`);
        break;
      case 'service':
        await axios.delete(`${API_BASE_URL}/item/service/${numericId}`);
        break;
      case 'rawMaterial':
        await axios.delete(`${API_BASE_URL}/item/material/${numericId}`);
        break;
      case 'productFinal':
        await axios.delete(`${API_BASE_URL}/item/product/${numericId}`);
        break;
      default:
        break;
    }

  } catch (error) {
    console.error('Erro em deleteScriptItem:', error);
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data?.message || 'Erro ao excluir item do script');
    }
    throw error;
  }
};

export const fetchCompaniesByStage = async (stageId: number, limit: number = 10, offset: number = 0, search?: string, id?: number): Promise<any[]> => {
  try {
    const page = Math.floor(offset / limit) + 1;
    const params: any = { page, limit, search, id };
    const { data } = await axios.get(`${API_BASE_URL}/company/by-service/${stageId}`, { params });
    return data.data || [];
  } catch (error) {
    console.warn('Erro ao buscar empresas por stage:', error);
    return [];
  }
};

export const fetchCompaniesByResource = async (resourceId: number, limit: number = 10, offset: number = 0, search?: string): Promise<any[]> => {
  try {
    const page = Math.floor(offset / limit) + 1;
    const { data } = await axios.get(`${API_BASE_URL}/company/by-resource/${resourceId}`, {
      params: { page, limit, search }
    });
    return data.data || [];
  } catch (error) {
    console.warn('Erro ao buscar empresas por resource:', error);
    return [];
  }
};

const extractNumericId = (id: string | number): number | null => {
  if (typeof id === 'number') {
    return id;
  }
  if (typeof id === 'string') {
    if (id.includes('_')) {
      const parts = id.split('_');
      const numericPart = parts.find(part => !isNaN(parseInt(part, 10)));
      return numericPart ? parseInt(numericPart, 10) : null;
    }
    const parsed = parseInt(id, 10);
    return isNaN(parsed) ? null : parsed;
  }
  return null;
};

const isTemporaryId = (id: string | number): boolean => {
  if (typeof id === 'number') {
    return id > 1000000;
  }
  if (typeof id === 'string') {
    const numId = parseInt(id, 10);
    return !isNaN(numId) && numId > 1000000;
  }
  return false;
};

export const updateScriptItemData = async (scriptId: number, item: any): Promise<void> => {
  try {
    const numericId = item.realId || extractNumericId(item.id);
    if (!numericId) return;

    if (isTemporaryId(item.id)) {
      return;
    }

    switch (item.type) {
      case 'resource': {
        await axios.patch(`${API_BASE_URL}/item/resource/${numericId}`, {
          resourceId: item.resource?.id,
          weight: item.weight ? parseFloat(String(item.weight)) : undefined,
          step: item.order ? parseInt(String(item.order), 10) : undefined,
          unitOfMeasurementId: item.unitOfMeasurementId ? parseInt(String(item.unitOfMeasurementId), 10) : undefined
        });
        break;
      }
      case 'service': {
        await axios.patch(`${API_BASE_URL}/item/service/${numericId}`, {
          serviceId: item.service?.id,
          companyId: item.company?.id || item.companyId,
          executionTime: item.executionTime,
          requiredQualification: item.requiredQualification,
          deliveryTime: item.deliveryTime ? String(item.deliveryTime) : undefined,
          step: item.order ? parseInt(String(item.order), 10) : undefined,
          unitOfMeasurementId: item.unitOfMeasurementId ? parseInt(String(item.unitOfMeasurementId), 10) : undefined
        });
        break;
      }
      case 'rawMaterial': {
        await axios.patch(`${API_BASE_URL}/item/material/${numericId}`, {
          materialId: item.rawMaterial?.id,
          weight: item.weight ? parseFloat(String(item.weight)) : undefined,
          idealMeasurements: item.idealMeasurements,
          step: item.order ? parseInt(String(item.order), 10) : undefined
        });
        break;
      }
      case 'productFinal': {
        await axios.patch(`${API_BASE_URL}/item/product/${numericId}`, {
          productId: item.productFinal?.id,
          productScript: item.productScript?.id ? String(item.productScript.id) : undefined,
          step: item.order ? parseInt(String(item.order), 10) : undefined
        });
        break;
      }
      default:
        break;
    }

  } catch (error) {
    console.error('Erro em updateScriptItemData:', error);
    if (error instanceof AxiosError) {
      throw new Error(error.response?.data?.message || 'Erro ao atualizar dados do item');
    }
    throw error;
  }
};